import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../../components/Config/api.service";
import { signalerErreur } from "../../../components/Utils/signalerErreur";
import { notify } from "../../../components/Utils/Notifications";
import { canDeleteSettlement } from "../../../components/Auth/permissions";
import { formatMoney } from "../../../components/Utils/FormatUtils";

const FinalSettlement = ({ setActiveTab, employee, currentContract, finalSettlementId, refreshRecap, handleDeleteFinalSettlement }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [departureReasons, setDepartureReasons] = useState([]);
  const [allIndemnities, setAllIndemnities] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [indemnities, setIndemnities] = useState([]);
  const [exitDate, setExitDate] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [finalSettlementData, setFinalSettlementData] = useState(null);
  const isDraft = finalSettlementData?.state === "draft" || !finalSettlementData;
  const isPaid = finalSettlementData?.state === "paid";
  // Un solde lié à un départ de l'espace RH se gère depuis ce départ : la Paie
  // le lit sans pouvoir le modifier (Yros/Kavero#8).
  const gereParRh = !!finalSettlementData?.gere_par_rh;
  const isEditable = isDraft && !gereParRh;
  const detail = finalSettlementData?.detail;
  const formatNumber = (value) => Number(value || 0).toLocaleString(dateLocale, { maximumFractionDigits: 2 });
  const formatRate = (rate) => `${Math.round(rate * 100)} %`;
  const manualFields = finalSettlementData?.manual_fields || [];
  const stateBadge = {
    validated: { label: t('employeeInfo.finalSettlement.validatedBadge'), className: "badge text-[var(--color-primary)]" },
    paid: { label: t('employeeInfo.finalSettlement.paidBadge'), className: "badge text-[var(--color-primary)]" },
  }[finalSettlementData?.state] || { label: t('employeeInfo.finalSettlement.draftBadge'), className: "badge text-[var(--color-orange)]" };
  // Factorise la récupération des données de base
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiService.getElement("/departure_reasons");
        setDepartureReasons(res.data.departure_reasons || []);
        setAllIndemnities(res.data.indemnity || []);
      } catch {
        setDepartureReasons([]);
        setAllIndemnities([]);
      }
    };
    fetchData();
  }, []);

  // Factorise la récupération du final settlement
  useEffect(() => {
    const fetchFinalSettlement = async () => {
      if (finalSettlementId) {
        const res = await apiService.getElementById("/final_settlement", finalSettlementId);
        const data = res.data.final_settlement;
        setFinalSettlementData(data);
        setSelectedReason(data.departure_reason_id?.toString() || "");
        setExitDate(data.exit_date || "");
        setNoticePeriod(data.notice_period ?? "");
      } else {
        setFinalSettlementData(null);
        setSelectedReason("");
        setExitDate("");
        setNoticePeriod("");
        setIndemnities([]);
      }
    };
    fetchFinalSettlement();
  }, [finalSettlementId]);

  

  // Fonction utilitaire pour filtrer les indemnités
  const getFilteredIndemnities = useCallback(() => {
    if (selectedReason && exitDate && allIndemnities.length > 0) {
      return allIndemnities
        .filter(i => i.departure_reason_ids.includes(Number(selectedReason)))
        .map(i => ({
          id: i.id,
          label: i.name,
          field: i.field_name,
          amount: finalSettlementData && i.field_name && finalSettlementData[i.field_name] !== undefined
            ? finalSettlementData[i.field_name]
            : ""
        }));
    }
    return [];
  }, [selectedReason, exitDate, allIndemnities, finalSettlementData]);

  useEffect(() => {
    setIndemnities(getFilteredIndemnities());
  }, [selectedReason, exitDate, allIndemnities, finalSettlementData, getFilteredIndemnities]);

  
  // Met à jour automatiquement les indemnités quand les dépendances changent
//   useEffect(() => {
//   const ready =
//     employee?.id &&
//     currentContract?.id &&
//     selectedReason &&
//     exitDate;

//   if (!ready) return;

//   const payload = {
//     employee_id: employee.id,
//     contract_id: currentContract.id,
//     departure_reason_id: selectedReason,
//     exit_date: exitDate,
//     action: finalSettlementData ? "save" : "create",
//     settlement_id: finalSettlementData?.id,
//   };

//   apiService.saveElement("/final_settlement", payload)
//     .then(res => setFinalSettlementData(res.data.final_settlement))
//     .catch(err => console.error("Erreur lors de la création/mise à jour du final settlement", err));
// }, [employee, currentContract, selectedReason, exitDate]);

const recalculateIndemnities = async () => {
  const ready =
    employee?.id &&
    currentContract?.id &&
    selectedReason &&
    exitDate;

  if (!ready) {
    notify(t('employeeInfo.finalSettlement.requiredFields'), "", 3);
    return;
  }

  const payload = {
    employee_id: employee.id,
    contract_id: currentContract.id,
    departure_reason_id: selectedReason,
    exit_date: exitDate,
    notice_period: noticePeriod === "" ? undefined : noticePeriod,
    action: "compute",
    settlement_id: finalSettlementData?.id,
  };

  try {
    const res = await apiService.saveElement("/final_settlement", payload);
    setFinalSettlementData(res.data.final_settlement);
    notify(t('employeeInfo.finalSettlement.recalculated'), "", 2);
  } catch (err) {
    signalerErreur(err, 'FinalSettlement.recalculateIndemnities');
    notify(err.response?.data?.error || t('employeeInfo.finalSettlement.recalculateError'), "", 3);
  }
};

// Revient au montant calculé pour une indemnité corrigée à la main
const resetAmount = async (field) => {
  const payload = {
    employee_id: employee.id,
    contract_id: currentContract.id,
    departure_reason_id: selectedReason,
    exit_date: exitDate,
    notice_period: noticePeriod === "" ? undefined : noticePeriod,
    action: "reset_amount",
    field,
    settlement_id: finalSettlementData?.id,
  };
  try {
    const res = await apiService.saveElement("/final_settlement", payload);
    setFinalSettlementData(res.data.final_settlement);
  } catch (err) {
    signalerErreur(err, 'FinalSettlement.resetAmount');
    notify(err.response?.data?.error || t('employeeInfo.finalSettlement.recalculateError'), "", 3);
  }
};

  
  const handleIndemnityChange = (index, field, value) => {
    setIndemnities(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  // const handleRemoveIndemnity = (index) => {
  //   setIndemnities(prev => prev.filter((_, i) => i !== index));
  // };

  
  const handleFinalSettlement = (action = "validate") => async (e) => {
  e.preventDefault();
  if (!selectedReason) return alert(t('employeeInfo.finalSettlement.selectReasonRequired'));
  if (!exitDate) return alert(t('employeeInfo.finalSettlement.selectExitDateRequired'));

  const modifiedIndemnities = indemnities
    // .filter(item => {
    //   if (!item.field || !finalSettlementData) return false;
    //   const initial = finalSettlementData[item.field];
    //   return String(item.amount) !== String(initial ?? "");
    // })
    .map(item => ({
      id: item.id,
      field: item.field,
      amount: item.amount
    }));

  const payload = {
    employee_id: employee.id,
    contract_id: currentContract.id,
    departure_reason_id: selectedReason,
    exit_date: exitDate,
    notice_period: noticePeriod === "" ? undefined : noticePeriod,
    indemnities: modifiedIndemnities,
    action,
    settlement_id: finalSettlementData?.id,
  };

  try {
    await apiService.saveElement("/final_settlement", payload);
    action === "validate"
      ? notify(t('employeeInfo.finalSettlement.validated'), "", 2)
      : action ==="draft" ? notify(t('employeeInfo.finalSettlement.backToDraft'), "", 2) : notify(t('employeeInfo.finalSettlement.saved'), "", 2);
    setActiveTab && refreshRecap();
  } catch (err) {
    notify(err.response?.data?.error || t('employeeInfo.finalSettlement.saveError'), "", 3);
  }
};
 

  return (
    <div className="bg-white min-h-screen p-6 md:p-10">
      <h2 className="text-3xl font-bold mb-8 text-red-500 flex justify-between items-center">{t('employeeInfo.finalSettlement.title')}
        <span className={stateBadge.className}>
          {stateBadge.label}
        </span>
      </h2>
      {gereParRh && (
        <div role="note" className="max-w-5xl mx-auto mb-8 rounded-lg border border-[var(--color-primary)] p-4 text-[var(--color-primary)]">
          {t('employeeInfo.finalSettlement.managedByHr')}
        </div>
      )}
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

            <div>
              <label className="block mb-1 font-medium">{t('employeeInfo.finalSettlement.departureReason')}</label>
              <select
                className="select select-bordered w-full"
                value={selectedReason}
                disabled={!isEditable}
                onChange={e => {
                  setSelectedReason(e.target.value);
                }}
              >
                <option value="" disabled>{t('employeeInfo.finalSettlement.chooseReason')}</option>
                {departureReasons.map(reason => (
                  <option key={reason.id} value={reason.id}>{reason.name}</option>
                ))}
              </select>
            </div>
            <div>
              </div>
            <div>
              <label className="block mb-1 font-medium">{t('employeeInfo.finalSettlement.entryDate')}</label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={currentContract?.DateStart || ""}
                readOnly
              />
            </div>
            {/* <div>
              <label className="block mb-1 font-medium">Date de sortie initialement prevu</label>
              <input
                type="date"
                className="input input-bordered w-full"
              />
            </div> */}
            <div>
              <label className="block mb-1 font-medium">{t('employeeInfo.finalSettlement.exitDate')}</label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={exitDate}
                disabled={!isEditable}
                onChange={e => setExitDate(e.target.value)}
              />
            </div>
            {indemnities.some(item => item.field === "indemnite_preavis_amount") && (
              <div>
                <label className="block mb-1 font-medium">{t('employeeInfo.finalSettlement.noticePeriod')}</label>
                <input
                  type="number"
                  min={0}
                  className="input input-bordered w-full"
                  value={noticePeriod}
                  disabled={!isEditable}
                  onChange={e => setNoticePeriod(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex gap-8 items-center mb-8 justify-between">
              <h3 className="text-lg font-bold">{t('employeeInfo.finalSettlement.indemnitiesSummary')}</h3>
              {isEditable && (
                <button
                  type="button"
                  className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary] hover:text-white"
                  onClick={recalculateIndemnities}
                >
                  {t('employeeInfo.finalSettlement.calculate')}
                </button>
              )}
          </div>

          <div>
            {indemnities.map((item, index) => (
             <div key={item.id} className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8 bg-gray-50 rounded-lg p-4">
                <span className="font-medium flex items-center gap-2">
                  {item.label}
                  {manualFields.includes(item.field) && (
                    <span className="badge text-[var(--color-orange)]">{t('employeeInfo.finalSettlement.manualBadge')}</span>
                  )}
                </span>
                <div className="flex items-center gap-3">
                {manualFields.includes(item.field) && isEditable && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline text-[--color-primary] hover:bg-[--color-primary] hover:text-white"
                    onClick={() => resetAmount(item.field)}
                  >
                    {t('employeeInfo.finalSettlement.resetAmount')}
                  </button>
                )}
                <input
                  type="number"
                  placeholder={t('employeeInfo.finalSettlement.amount')}
                  className="input input-bordered"
                  min={0}
                  value={item.amount}
                  disabled={!isEditable}
                  onChange={e => handleIndemnityChange(index, "amount", e.target.value)}
                />
                </div>
                {/* {indemnities.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-xs btn-circle btn-outline btn-error"
                    title="Supprimer"
                    onClick={() => handleRemoveIndemnity(index)}
                  >×</button>
                )} */}
              </div>
            ))}
          </div>
          {/* Boutton pour a */}
          {/* <button
            type="button"
            className="text-[--color-primary] mt-2 mb-8 flex items-center gap-1"
            onClick={handleAddIndemnity}
          >
            <span className="text-xl font-bold">+</span>
            <span className="underline">Ajouter une indemnité</span>
          </button> */}

          {/* Détail du calcul, replié par défaut */}
          {detail && indemnities.length > 0 && (
            <div className="border rounded-lg mb-8">
              <button
                type="button"
                className="w-full flex justify-between items-center p-4 font-medium"
                onClick={() => setShowDetail(!showDetail)}
              >
                {showDetail ? t('employeeInfo.finalSettlement.hideDetail') : t('employeeInfo.finalSettlement.showDetail')}
                <span>{showDetail ? "▲" : "▼"}</span>
              </button>
              {showDetail && (
                <div className="px-4 pb-4 flex flex-col gap-4 text-sm">
                  {indemnities.map(item => {
                    const d = detail[item.field];
                    if (!d) return null;
                    const rows = [];
                    if (d.brackets) {
                      rows.push([t('employeeInfo.finalSettlement.detailSeniority'), t('employeeInfo.finalSettlement.detailYears', { count: formatNumber(d.worked_years) })]);
                      rows.push([t('employeeInfo.finalSettlement.detailAverageSalary'), d.average_on_wage
                        ? `${formatMoney(d.average_salary)} (${t('employeeInfo.finalSettlement.detailContractWage')})`
                        : `${formatMoney(d.average_salary_total)} ÷ ${d.average_salary_months} = ${formatMoney(d.average_salary)}`]);
                      rows.push([t('employeeInfo.finalSettlement.detailReferencePeriod'), t('employeeInfo.finalSettlement.detailPeriodValue', { start: d.period_start, end: d.period_end })]);
                      d.brackets.forEach(b => rows.push([
                        t('employeeInfo.finalSettlement.detailBracket', { rate: formatRate(b.rate) }),
                        `${formatNumber(b.years)} × ${formatMoney(d.average_salary)} × ${formatRate(b.rate)} = ${formatMoney(b.amount)}`,
                      ]));
                    } else if (d.divisor) {
                      rows.push([t('employeeInfo.finalSettlement.detailReferencePeriod'), t('employeeInfo.finalSettlement.detailPeriodValue', { start: d.period_start, end: d.period_end })]);
                      rows.push([t('employeeInfo.finalSettlement.detailTotalBrut'), `${formatMoney(d.total_brut)} ÷ ${d.divisor}`]);
                    } else if (d.rate) {
                      rows.push([t('employeeInfo.finalSettlement.detailTotalEarned'), formatMoney(d.total_earned)]);
                      rows.push([t('employeeInfo.finalSettlement.detailRate'), `(${formatMoney(d.total_earned)} + ${formatMoney(d.conge_amount)}) × ${formatRate(d.rate)}`]);
                    } else if (d.notice_days !== undefined) {
                      rows.push([t('employeeInfo.finalSettlement.noticePeriod'), `${d.notice_days}`]);
                      rows.push([t('employeeInfo.finalSettlement.detailFormula'), `${formatMoney(d.average_salary)} ÷ 30 × ${d.notice_days}`]);
                    }
                    return (
                      <div key={item.field}>
                        <p className="font-semibold mb-1">{item.label}</p>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                          {rows.map(([label, value]) => (
                            <React.Fragment key={label}>
                              <dt className="text-gray-500">{label}</dt>
                              <dd>{value}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Solde à verser */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8 bg-gray-50 rounded-lg p-4">
            <span className="font-bold text-xl">{t('employeeInfo.finalSettlement.balanceDue')}</span>
            <span className="font-bold text-2xl text-[--color-primary]">
              {formatMoney(indemnities.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
            </span>
          </div>

        </div>
      </div>
      <div className="flex justify-end gap-4 mt-8">
        {!isEditable ? (
          <>
            {!isPaid && !gereParRh && (
              <button
                type="button"
                className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary]"
                onClick={handleFinalSettlement("draft")}
              >
                {t('employeeInfo.finalSettlement.backToDraftButton')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setActiveTab("Contrats")}
              className="btn btn-outline btn-error"
            >
              {t('employeeInfo.finalSettlement.back')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary]"
              onClick={handleFinalSettlement("save")}
            >
              {t('employeeInfo.finalSettlement.save')}
            </button>
            <button
              onClick={handleFinalSettlement("validate")}
              type="submit"
              className="btn bg-[--color-primary] text-white hover:bg-[--bg-hover]"
            >
              {t('employeeInfo.finalSettlement.validate')}
            </button>
            {/* Avec un identifiant, le bouton supprime côté serveur : ouvert à
                la RH par `require_group` côté Odoo (Yros/Support#50). Sans
                identifiant, il ne fait que quitter un solde pas encore enregistré. */}
            {(!finalSettlementId || canDeleteSettlement()) && (
              <button
                type="button"
                onClick={finalSettlementId ? handleDeleteFinalSettlement : () => setActiveTab("Contrats")}
                className="btn btn-outline btn-error"
              >
                {t('employeeInfo.finalSettlement.delete')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinalSettlement;